<template>
  <button v-if="!hasTo" class="apple-ui-button" :class="buttonClasses" v-bind="$attrs" :type="type" :disabled="disabled" v-on="$listeners">
    <span v-if="$slots.leading" class="apple-ui-button-leading"><slot name="leading" /></span><span class="apple-ui-button-label"><slot /></span>
  </button>
  <span v-else-if="disabled" class="apple-ui-button" :class="buttonClasses" v-bind="$attrs" role="link" aria-disabled="true" tabindex="-1">
    <span v-if="$slots.leading" class="apple-ui-button-leading"><slot name="leading" /></span><span class="apple-ui-button-label"><slot /></span>
  </span>
  <router-link v-else :to="to" :replace="replace" custom v-slot="{ href, navigate }">
    <a class="apple-ui-button" :class="buttonClasses" v-bind="$attrs" :href="href" v-on="linkListeners" @click="onLinkClick($event, navigate)">
      <span v-if="$slots.leading" class="apple-ui-button-leading"><slot name="leading" /></span><span class="apple-ui-button-label"><slot /></span>
    </a>
  </router-link>
</template>

<script>
export default {
  name: 'AppleButton',
  inheritAttrs: false,
  props: {
    variant: { type: String, default: 'filled', validator: value => ['filled', 'subtle', 'plain'].includes(value) },
    iconOnly: Boolean,
    selected: Boolean,
    disabled: Boolean,
    type: { type: String, default: 'button', validator: value => ['button', 'submit', 'reset'].includes(value) },
    to: { type: [String, Object], default: null },
    replace: Boolean,
  },
  computed: {
    hasTo() { return this.to !== null && this.to !== undefined; },
    buttonClasses() { return [this.variant, { 'icon-only': this.iconOnly, 'is-disabled': this.disabled, 'is-selected': this.selected }]; },
    linkListeners() { const listeners = { ...this.$listeners }; delete listeners.click; return listeners; },
  },
  methods: {
    onLinkClick(event, navigate) {
      const listener = this.$listeners.click;
      if (Array.isArray(listener)) listener.forEach(fn => fn(event));
      else if (listener) listener(event);
      if (!event.defaultPrevented) navigate(event);
    },
  },
};
</script>

<style scoped>
.apple-ui-button {
  display: inline-flex; align-items: center; justify-content: center; gap: var(--apple-ui-space-2, 8px);
  box-sizing: border-box; min-width: 0; min-height: var(--apple-ui-control-height, 32px); padding: 5px 12px;
  border: 0; border-radius: var(--apple-ui-radius-control, 7px); font: inherit; font-size: 13px; font-weight: 600;
  line-height: 1.25; text-decoration: none; cursor: pointer; user-select: none; -webkit-tap-highlight-color: transparent;
}
.apple-ui-button.filled { color: #fff; background: var(--apple-ui-accent, var(--color-primary)); }
.apple-ui-button.subtle { color: var(--apple-ui-text, var(--color-text)); background: color-mix(in srgb, var(--apple-ui-text, var(--color-text)) 7%, transparent); }
.apple-ui-button.plain { color: var(--apple-ui-accent, var(--color-primary)); background: transparent; }
.apple-ui-button:not(.is-disabled):hover { filter: brightness(.96); }
.apple-ui-button.plain:not(.is-disabled):hover { background: color-mix(in srgb, var(--apple-ui-text, var(--color-text)) 6%, transparent); }
.apple-ui-button:not(.is-disabled):active { filter: brightness(.9); }
.apple-ui-button:focus:not(:focus-visible) { outline: none; }
/* Keep keyboard feedback inside the existing control box. An outer outline
   makes a 44px touch target look like an accidental blue card and changes
   the visual rhythm of adjacent controls. */
.apple-ui-button.apple-ui-button:focus-visible { outline: none; box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--apple-ui-accent, var(--color-primary)) 82%, transparent); }
.apple-ui-button.apple-ui-button.filled:focus-visible { box-shadow: inset 0 0 0 2px rgb(255 255 255 / .88); }
.apple-ui-button.apple-ui-button.is-disabled,
.apple-ui-button.apple-ui-button:disabled { color:var(--apple-ui-disabled-text,color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 38%,var(--color-body-bg))); background:var(--apple-ui-disabled-surface,color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 8%,var(--color-body-bg))); opacity:1; cursor:default; filter:none; }
.apple-ui-button.apple-ui-button.plain.is-disabled,
.apple-ui-button.apple-ui-button.plain:disabled { background:transparent; }
.apple-ui-button.icon-only { width: var(--apple-ui-control-height, 32px); min-width: var(--apple-ui-control-height, 32px); height: var(--apple-ui-control-height, 32px); padding: 0; }
.apple-ui-button.icon-only.is-selected:not(.is-disabled) { color:var(--apple-ui-accent,var(--color-primary)); background:color-mix(in srgb,var(--apple-ui-accent,var(--color-primary)) 14%,transparent); }
.apple-ui-button-label, .apple-ui-button-leading { display: inline-flex; align-items: center; justify-content: center; min-width: 0; }
.apple-ui-button-label { min-width: 0; overflow-wrap: anywhere; white-space: normal; text-align: center; }
.apple-ui-button-leading { flex: none; }
.apple-ui-button-leading ::v-deep svg, .apple-ui-button.icon-only ::v-deep svg { width: 16px; height: 16px; }
@media (forced-colors: active) {
  .apple-ui-button { border: 1px solid ButtonText; }
  .apple-ui-button.apple-ui-button:focus-visible { outline: 2px solid Highlight; outline-offset: -2px; }
}
</style>
